import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, KeyRound } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [requires2fa, setRequires2fa] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password, totp || undefined);
      if (res.requires_2fa) {
        setRequires2fa(true);
        toast.info("Enter your 2FA code");
      } else {
        toast.success("Welcome back");
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 vq-dot-bg opacity-30" />
        <div className="relative">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-slate-900" />
            </div>
            <span className="font-display font-extrabold text-xl">VitaQMS</span>
          </Link>
        </div>
        <div className="relative">
          <div className="vq-overline text-slate-400 mb-3">Trusted by regulatory teams</div>
          <h2 className="font-display font-bold text-3xl leading-tight">
            &ldquo;We compressed 4 months of QMS bootstrap into 3 days.&rdquo;
          </h2>
          <p className="mt-6 text-slate-300">— Dr. Elena Voss, VitaCen Team</p>
        </div>
        <div className="relative flex gap-6 text-xs font-mono text-slate-400">
          <span>FDA</span><span>EU MDR</span><span>CDSCO</span><span>MDSAP</span>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-slate-900 rounded flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 text-white" />
              </div>
              <span className="font-display font-extrabold text-xl">VitaQMS</span>
            </Link>
          </div>

          <div className="vq-overline mb-3">Sign in</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-8">
            Welcome back.
          </h1>

          <Button
            data-testid="google-signin-btn"
            onClick={handleGoogle}
            variant="outline"
            className="w-full h-11 border-slate-300 font-medium mb-6"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09 0-.73.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative my-6 flex items-center">
            <div className="flex-1 border-t border-slate-200" />
            <span className="px-3 text-xs font-mono text-slate-400">OR EMAIL</span>
            <div className="flex-1 border-t border-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm font-semibold text-slate-700">Email</Label>
              <Input
                data-testid="login-email-input"
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11 bg-white"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-semibold text-slate-700">Password</Label>
              <Input
                data-testid="login-password-input"
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11 bg-white"
                placeholder="••••••••"
              />
            </div>
            {requires2fa && (
              <div>
                <Label htmlFor="totp" className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <KeyRound className="w-4 h-4" /> 2FA Code
                </Label>
                <Input
                  data-testid="login-totp-input"
                  id="totp"
                  required
                  value={totp}
                  onChange={(e) => setTotp(e.target.value)}
                  className="mt-1.5 h-11 bg-white font-mono tracking-widest"
                  placeholder="123456"
                  maxLength={6}
                />
              </div>
            )}
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-slate-900 hover:bg-[#0055FF] text-white font-semibold rounded-md"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-600 text-center">
            No account?{" "}
            <Link to="/register" className="font-semibold text-slate-900 hover:text-[#0055FF]">
              Start free
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
